import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { IconButton } from "@mui/material";
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
} from "@mui/icons-material";
import { toastMessage } from "../../utils/toastMessage";
import Loader from "../../comman/Loader";
import { segment } from "../../routes/route";
import Pagination from "react-js-pagination";
import NotFound from "../../components/not-found/NotFound";
import common from "../../routes/common";
import findSerialNumber from "../../utils/findSerialNumber";
import {
  getSegmentApi,
  getSegmentUsersApi,
  getSegmentLogsApi,
  backfillSegmentApi,
  sendInteraktEventApi,
} from "../../services/segmentManagement";

const { NOT_FOUND } = common;
const { SEGMENT_LIST } = segment;

const SegmentDetail = () => {
  const [sendingUserId, setSendingUserId] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);

  const handleSendAllInteraktEvents = async () => {
    setSendingAll(true);
    let successCount = 0, failedCount = 0;
    for (const user of users) {
      try {
        const payload = {
          event_name: 'registration_completed',
          event_params: {
            interakt_tag: segmentData.interakt_tag,
            user_id: user._id,
            phone: user.details?.phone,
            zodiac_sign: user.details?.zodiac,
            gender: user.details?.gender,
            date_of_birth: user.details?.dob,
            registration_date: user.createdAt,
            problem: user.details?.problems,
          },
          userId: user._id,
          platform: 'web',
          timestamp: new Date().toISOString(),
        };
        await sendInteraktEventApi(payload);
        successCount++;
      } catch (err) {
        failedCount++;
      }
    }
    setSendingAll(false);
    if (failedCount === 0) {
      toastMessage('Events sent successfully to all users!', 'success');
    } else {
      toastMessage(`${successCount} succeeded, ${failedCount} failed.`, failedCount > 0 ? 'error' : 'success');
    }
  };

  const handleSendInteraktEvent = async (user) => {
    setSendingUserId(user._id);
    try {
      // Compose the event payload
      const payload = {
        event_name: 'registration_completed',
        event_params: {
          interakt_tag: segmentData.interakt_tag,
          user_id: user._id,
          phone: user.details?.phone,
        },
        userId: user._id,
        platform: 'web',
        timestamp: new Date().toISOString(),
      };
      await sendInteraktEventApi(payload);
      toastMessage('Event sent successfully!', 'success');
    } catch (err) {
      toastMessage(err.response?.data?.message || err.message || 'Failed to send event', 'error');
    }
    setSendingUserId(null);
  };
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [segmentData, setSegmentData] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [usersPage, setUsersPage] = useState(1);
  const [logsPage, setLogsPage] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const [logsTotal, setLogsTotal] = useState(0);
  const limit = 20;

  useEffect(() => {
    if (id) {
      fetchSegment();
      fetchUsers();
      fetchLogs();
    }
  }, [id, usersPage, logsPage]);

  const fetchSegment = async () => {
    try {
      setLoading(true);
      const segmentData = await getSegmentApi(id);
      setSegmentData(segmentData);
    } catch (error) {
      toastMessage(error.response?.data?.message || error.message || "Error fetching segment", "error");
      navigate(SEGMENT_LIST);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      // Fetch real-time users (evaluated based on current segment logic)
      const data = await getSegmentUsersApi(id, { page: usersPage, limit });
      if (data.success) {
        setUsers(data.data || []);
        setUsersTotal(data.pagination?.total || 0);
      }
    } catch (error) {
      toastMessage(error.response?.data?.message || error.message || "Error fetching users", "error");
    }
  };

  const fetchLogs = async () => {
    try {
      const data = await getSegmentLogsApi(id, { page: logsPage, limit });
      if (data.success) {
        setLogs(data.data || []);
        setLogsTotal(data.pagination?.total || 0);
      }
    } catch (error) {
      toastMessage(error.response?.data?.message || error.message || "Error fetching logs", "error");
    }
  };

  const handleBackfill = async () => {
    try {
      const data = await backfillSegmentApi(id);
      if (data.success) {
        toastMessage(`Backfill queued for ${data.queued || 0} users`, "success");
        fetchSegment();
      } else {
        toastMessage(data.message || "Error starting backfill", "error");
      }
    } catch (error) {
      toastMessage(error.response?.data?.message || error.message || "Error starting backfill", "error");
    }
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      draft: "badge badge-secondary",
      ready: "badge badge-info",
      applied: "badge badge-success",
      paused: "badge badge-warning",
    };
    return statusClasses[status] || "badge badge-secondary";
  };

  const getSyncStatusBadge = (status) => {
    const statusClasses = {
      synced: "badge badge-success",
      pending: "badge badge-warning",
      failed: "badge badge-danger",
      not_synced: "badge badge-secondary",
    };
    return statusClasses[status] || "badge badge-secondary";
  };

  const getLogActionBadge = (action) => {
    const actionClasses = {
      added: "badge badge-success",
      removed: "badge badge-danger",
      evaluated: "badge badge-info",
      sync_success: "badge badge-success",
      sync_failed: "badge badge-danger",
      error: "badge badge-danger",
    };
    return actionClasses[action] || "badge badge-secondary";
  };

  if (loading && !segmentData) {
    return <Loader />;
  }

  if (!segmentData) {
    return null;
  }

  return (
    <div className="WrapperArea">
      <div className="WrapperBox">
        <div className="TitleBox">
          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <Link to={SEGMENT_LIST}>
              <IconButton>
                <BackIcon />
              </IconButton>
            </Link>
            <h4 className="Title">{segmentData.name}</h4>
          </div>
          <div>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={fetchSegment}
              style={{ marginRight: "10px" }}
            >
              <RefreshIcon style={{ fontSize: "18px", marginRight: "5px" }} />
              Refresh
            </button>
            {/* <button
              type="button"
              className="btn btn-primary"
              onClick={handleBackfill}
            >
              <PlayIcon style={{ fontSize: "18px", marginRight: "5px" }} />
              Backfill
            </button> */}
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: "20px",
            marginBottom: "20px",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              flex: "1",
              minWidth: "300px",
              maxWidth: "100%",
            }}
          >
            <div className="FilterBox FilterArea">
             
              <div style={{ marginTop: "15px" }}>
              <h5 style={{ marginBottom: "20px", fontSize: "18px", fontWeight: "700" }}>
                Segment Information
              </h5>
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>Name</label>
                  <p style={{ margin: "5px 0 0 0", fontSize: "16px", fontWeight: "600" }}>
                    {segmentData.name}
                  </p>
                </div>
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>
                    Description
                  </label>
                  <p style={{ margin: "5px 0 0 0", fontSize: "16px" }}>
                    {segmentData.description || "No description"}
                  </p>
                </div>
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>
                    Interakt Tag
                  </label>
                  <div style={{ marginTop: "5px" }}>
                    <span>{segmentData.interakt_tag}</span>
                  </div>
                </div>
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>Status</label>
                  <div style={{ marginTop: "5px" }}>
                    <span>{segmentData.status}</span>
                  </div>
                </div>
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>
                    Sync Status
                  </label>
                  <div style={{ marginTop: "5px" }}>
                    <span>
                      {segmentData.interakt_sync_status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            style={{
              flex: "1",
              minWidth: "300px",
              maxWidth: "100%",
            }}
          >
            <div className="FilterBox FilterArea">
             
              <div style={{ marginTop: "15px" }}>
              <h5 style={{ marginBottom: "20px", fontSize: "18px", fontWeight: "700" }}>
                Statistics
              </h5>
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>
                    Matching Users
                  </label>
                  <p style={{ margin: "5px 0 0 0", fontSize: "28px", fontWeight: "700", color: "#20547d" }}>
                    {usersTotal || 0}
                  </p>
                </div>
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>Last Run</label>
                  <p style={{ margin: "5px 0 0 0", fontSize: "16px" }}>
                    {segmentData.last_run_date
                      ? new Date(segmentData.last_run_date).toLocaleString()
                      : "Never"}
                  </p>
                </div>
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>
                    Created By
                  </label>
                  <p style={{ margin: "5px 0 0 0", fontSize: "16px" }}>
                    {segmentData.created_by?.name || "Unknown"}
                  </p>
                </div>
                <div style={{ marginBottom: "15px" }}>
                  <label style={{ fontSize: "14px", color: "#666", fontWeight: "500" }}>
                    Created At
                  </label>
                  <p style={{ margin: "5px 0 0 0", fontSize: "16px" }}>
                    {new Date(segmentData.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="FilterBox FilterArea">
          <div className="banner_button_wrapper" style={{ marginBottom: "20px" }}>
            <button
              type="button"
              className={`_button ${tabValue === 0 && "banner_active"}`}
              onClick={() => setTabValue(0)}
            >
              Users
            </button>
            <button
              type="button"
              className={`_button ${tabValue === 1 && "banner_active"}`}
              onClick={() => setTabValue(1)}
            >
              Logs
            </button>
            <button
              type="button"
              className={`_button ${tabValue === 2 && "banner_active"}`}
              onClick={() => setTabValue(2)}
            >
              Logic
            </button>
          </div>

        </div>

        {tabValue === 0 && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                <button
                  type="button"
                  className="btn btn-success"
                  style={{ fontSize: '14px', padding: '6px 18px' }}
                  onClick={handleSendAllInteraktEvents}
                  disabled={sendingAll}
                >
                  {sendingAll ? 'Sending Events...' : 'Send Event to All'}
                </button>
              </div>
              <div style={{
                marginBottom: "15px",
                padding: "12px 16px",
                backgroundColor: "#e3f2fd",
                border: "1px solid #90caf9",
                borderRadius: "6px",
                display: "flex",
                alignItems: "center",
                gap: "10px"
              }}>
                <span style={{ fontSize: "16px" }}>🔄</span>
                <span style={{ fontSize: "14px", color: "#1976d2", fontWeight: "500" }}>
                  Showing real-time users based on current segment logic
                </span>
              </div>
              <div className="content-table">
                <table>
                  <thead>
                    <tr>
                      <th>S. No.</th>
                      <th>User Name</th>
                      <th>Phone</th>
                      <th>Email</th>
                      <th>Added At</th>
                      <th>Synced</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length > 0 ? (
                      users.map((userSegment, index) => (
                        <tr key={userSegment._id}>
                          <td>{findSerialNumber(index, usersPage, limit)}</td>
                          <td>{userSegment.details?.name || "User"}</td>
                          <td>{userSegment?.details?.phone || "N/A"}</td>
                          <td>{userSegment.details?.email || "N/A"}</td>
                          <td>{new Date(userSegment.userCreatedAt).toLocaleString()}</td>
                          <td>
                            <span>
                              {userSegment.interakt_synced ? "Yes" : "No"}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <NotFound cols={6} msg="No users in this segment!" icon={NOT_FOUND} />
                    )}
                  </tbody>
                </table>
              </div>
              {usersTotal > limit && (
                <div className="Pagination">
                  <Pagination
                    activePage={usersPage}
                    itemsCountPerPage={limit}
                    totalItemsCount={usersTotal}
                    pageRangeDisplayed={5}
                    onChange={setUsersPage}
                    itemClass="page-item"
                    linkClass="page-link"
                  />
                </div>
              )}
            </div>
          )}

          {tabValue === 1 && (
            <div>
              <div className="content-table">
                <table>
                  <thead>
                    <tr>
                      <th>S. No.</th>
                      <th>Type</th>
                      <th>Action</th>
                      <th>Message</th>
                      <th>User ID</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.length > 0 ? (
                      logs.map((log, index) => (
                        <tr key={log._id}>
                          <td>{findSerialNumber(index, logsPage, limit)}</td>
                          <td>
                            <span className="badge badge-info">{log.log_type}</span>
                          </td>
                          <td>
                            <span className={getLogActionBadge(log.action)}>{log.action}</span>
                          </td>
                          <td>{log.message}</td>
                          <td>{log.user_id || "N/A"}</td>
                          <td>{new Date(log.createdAt).toLocaleString()}</td>
                        </tr>
                      ))
                    ) : (
                      <NotFound cols={6} msg="No logs found!" icon={NOT_FOUND} />
                    )}
                  </tbody>
                </table>
              </div>
              {logsTotal > limit && (
                <div className="Pagination">
                  <Pagination
                    activePage={logsPage}
                    itemsCountPerPage={limit}
                    totalItemsCount={logsTotal}
                    pageRangeDisplayed={5}
                    onChange={setLogsPage}
                    itemClass="page-item"
                    linkClass="page-link"
                  />
                </div>
              )}
            </div>
          )}

          {tabValue === 2 && (
            <div>
              <h5 style={{ marginBottom: "15px", fontSize: "18px", fontWeight: "700" }}>
                Segment Logic Structure
              </h5>
              <div
                style={{
                  backgroundColor: "#f5f5f5",
                  padding: "15px",
                  borderRadius: "5px",
                  overflow: "auto",
                  maxHeight: "500px",
                }}
              >
                <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {JSON.stringify(segmentData.logic, null, 2)}
                </pre>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default SegmentDetail;
